import type { CaptureProfile, RawReading, NormalizedRect, MetricRecognitionStatus, ExpRateChartGap, ExpRateChartSnapshot, ExpReadoutSnapshot, ExpRecognitionIssue, ExpRecognitionProgress, RecognitionContext, RecognitionMetricKey } from '@/domain/types'
import type { PresenceBreakReason } from '@/core/source-continuity'
import type { LayoutSearchDiagnostic } from '@/presets/types'
import { buildInfo } from './build-info'
import type { OcrStartupDiagnostic } from './ocr-startup'
import type { CapturePreparationDiagnostic } from './capture-preparation'

export type PerformanceStage = 'frame' | 'crop' | 'preprocess' | 'ocr' | 'ocr-initialize' | 'chart' | 'save' | 'long-task'
  | 'widget-render' | 'widget-layout' | 'widget-composition' | 'widget-heartbeat'
export type WidgetRefreshReason = 'initial' | 'snapshot' | 'timer' | 'explicit' | 'command'
export interface WidgetPerformanceContext {
  open: boolean
  mode: 'pip' | 'popup' | 'preview'
  skin: string
  mainVisibility: DocumentVisibilityState
}
export type PerformanceResource = 'live-stores' | 'previews' | 'charts' | 'pending-saves' | 'frame-sources' | 'layout-workers' | 'ocr-model-urls'
export type DiagnosticError = 'capture-error' | 'frame-error' | 'image-error' | 'ocr-timeout' | 'ocr-engine-error' | 'ocr-initialize-error' | 'ocr-unknown-error' | 'cancelled'
export interface WidgetFailure {
  phase: 'controls' | 'content' | 'layout' | 'command' | 'heartbeat'
  errorKind: string
  skin: string
  durationMs: number
  sessionStatus: string
  isEnding: boolean
  isStarting: boolean
  reloadBlocked: boolean
  pendingCommand: 'primary' | 'end' | null
  contentFailures: number
}

type Counter = 'ocrInitializations' | 'ocrResets' | 'unchangedSkips' | 'recoveryBackoffs'
interface Measurement { at: number; ms: number }
interface Timing { count: number; totalMs: number; maxMs: number; recent: Measurement[] }
interface ExpChartEvent extends ExpRateChartGap {
  to: number
  observedAt: number
  durationMs: number
  readoutReason: ExpReadoutSnapshot['reason']
  recoveredAt: number | null
  recoverySourceAt: number | null
  recognitionIssue?: ExpRecognitionIssue
}
type ExpRecognitionEvent = ExpRecognitionIssue & { lastSourceAt: number; recoveredAt: number | null; recoverySourceAt: number | null }

interface MesosRecognitionDiagnostic {
  presetId: string
  profileId: string
  generation: number
  sourceRevision: number
  mode: 'manual' | 'automatic'
  roi: NormalizedRect | null
  status: MetricRecognitionStatus
  observedAt: number
  frameAt: number
  sourceAt: number | null
  lastQueuedAt: number | null
  lastScheduledAt: number | null
  lastCompletedAt: number | null
  completionStatus: MetricRecognitionStatus | null
  rawText: string
  failureReason: string | null
  confidence: number | null
  value: number | null
}
type ExpReadoutDiagnostic = Pick<ExpReadoutSnapshot, 'status' | 'reason' | 'scope' | 'incomplete' | 'incompleteSince' | 'incompleteReason'>
export interface CaptureRecoveryEvent {
  reason: 'scheduler-stall' | 'media-interruption' | 'page-resume' | 'manual'
  action: 'detected' | 'rebind-playback' | 'resumed' | 'needs-user-action' | 'cancelled' | 'stalled-again' | 'frames-resumed' | 'exp-confirmed' | 'recognition-stalled' | 'retry-recognition' | 'confirmation-slow'
  generation: number
  outcome?: 'recovered' | 'timeout' | 'cancelled' | 'unavailable'
  capture?: CapturePerformanceInfo | null
  health?: import('./frame-scheduler').FrameSchedulerHealth | null
}
export interface CapturePerformanceInfo {
  capabilities?: { imageCapture: boolean; videoFrameCallback: boolean; playbackQuality: boolean }
  frameRefresh?: import('./capture-service').FrameRefreshDiagnostic
  status: string
  mediaState?: string
  paused?: boolean
  readyState?: number
  currentTime?: number
  trackReadyState?: string | null
  trackMuted?: boolean | null
  totalVideoFrames?: number | null
  decodedFrames?: number | null
  mozPresentedFrames?: number | null
  requestedFpsLimit: number
  reportedFps: number | null
  width: number | null
  height: number | null
  surface: string | null
  videoWidth?: number | null
  videoHeight?: number | null
  constraintTier?: string | null
  constraintTargetFps?: number | null
  constraintApplied?: boolean
  constraintMaxFps?: number | null
  constraintFailure?: string | null
  playbackReportedFps?: number | null
  playbackConstraintMaxFps?: number | null
}

export interface RecordingDiagnostic {
  captureRecovery?: { state: string; confirmingSince: number | null; minimumSourceAt: number | null; slow: boolean; blockingReason: string | null;
    failureReason: string | null; confirmation: ExpReadoutSnapshot['confirmation'] | null }
  sessionId: string | null
  status: string
  startedAt: number | null
  activeDurationMs: number | null
  starting: boolean
  ending: boolean
  startStage: string | null
  selectionId: number
  captureGeneration: number
  pipelineGeneration: number
  pipelineActive: boolean
  presetId: string | null
  profileId: string | null
  readoutStatus: string | null
  chartStatus: string | null
  chartContinuousMs: number | null
  chartEndpointAt: number | null
}
type DeliveryOutcome = 'ingested' | 'not-recording' | 'old-generation' | 'old-pipeline' | 'disposed'
type RecordingEvent = { kind: 'session' | 'pipeline' | 'exp-accounting'; reason: string; sessionId: string | null; generation: number }

const WINDOW_MS = 60_000
const MAX_SAMPLES = 240
const MAX_EVENTS = 40
function summarizeDelays(values: number[]) {
  const sorted = values.filter(n => Number.isFinite(n) && n >= 0).sort((a, b) => a - b)
  return { count: sorted.length, p50Ms: sorted[Math.ceil(sorted.length * .5) - 1] ?? null,
    p95Ms: sorted[Math.ceil(sorted.length * .95) - 1] ?? null, maxMs: sorted.at(-1) ?? null }
}
const stages: PerformanceStage[] = ['frame', 'crop', 'preprocess', 'ocr', 'ocr-initialize', 'chart', 'save', 'long-task',
  'widget-render', 'widget-layout', 'widget-composition', 'widget-heartbeat']

/** Bounded, in-memory diagnostics. No images or URLs; OCR text is length-limited. */
export class PerformanceDiagnostics {
  private resourceDeliveryScope = ''
  private resourceDeliveries: Record<'hp' | 'mp', { sourceAt: number; completedAt: number; deliveredAt: number }[]> = { hp: [], mp: [] }
  observeResourceDelivery(scope: string, raw: RawReading, now = Date.now()): void {
    if (scope !== this.resourceDeliveryScope) { this.resourceDeliveryScope = scope; this.resourceDeliveries = { hp: [], mp: [] } }
    for (const metric of ['hp', 'mp'] as const) {
      const rounds = this.resourceDeliveries[metric] = this.resourceDeliveries[metric].filter(r => now - r.deliveredAt <= WINDOW_MS)
      const completion = raw.diagnostics.metrics?.[metric]?.completion
      if (!completion || completion.completedAt === undefined || !Number.isFinite(completion.completedAt)
        || rounds.some(r => r.sourceAt === completion.capturedAt)) continue
      rounds.push({ sourceAt: completion.capturedAt, completedAt: completion.completedAt, deliveredAt: now })
      if (rounds.length > MAX_SAMPLES) rounds.shift()
    }
  }
  private resourceDeliverySnapshot(now: number) {
    const snapshot = (metric: 'hp' | 'mp') => {
      const rounds = this.resourceDeliveries[metric].filter(r => now - r.deliveredAt <= WINDOW_MS)
      return { rounds: rounds.map(r => ({ ...r })),
        completionToDelivery: summarizeDelays(rounds.map(r => r.deliveredAt - r.completedAt)) }
    }
    return { hp: snapshot('hp'), mp: snapshot('mp') }
  }
  private expDeliveryScope = ''
  private expDeliveries: { sourceAt: number; scheduledAt?: number; completedAt?: number; deliveredAt: number; acceptedAt?: number }[] = []
  observeExpDelivery(scope: string, raw: RawReading, now = Date.now()): void {
    if (scope !== this.expDeliveryScope) { this.expDeliveryScope = scope; this.expDeliveries = [] }
    this.expDeliveries = this.expDeliveries.filter(r => now - r.deliveredAt <= WINDOW_MS)
    const completion = raw.diagnostics.metrics?.exp?.completion
    if (!completion || this.expDeliveries.some(r => r.sourceAt === completion.capturedAt)) return
    this.expDeliveries.push({ sourceAt: completion.capturedAt, scheduledAt: completion.scheduledAt,
      completedAt: completion.completedAt, deliveredAt: now })
    if (this.expDeliveries.length > MAX_SAMPLES) this.expDeliveries.shift()
  }
  observeExpStatisticsAdoption(scope: string, sourceAt: number | null | undefined, now = Date.now()): void {
    if (scope !== this.expDeliveryScope || sourceAt == null) return
    const row = this.expDeliveries.find(r => r.sourceAt === sourceAt)
    if (row && row.acceptedAt === undefined) row.acceptedAt = now
  }
  private expDeliverySnapshot(now: number) {
    const rounds = this.expDeliveries.filter(r => now - r.deliveredAt <= WINDOW_MS)
    return { rounds: rounds.map(r => ({ ...r })),
      queue: summarizeDelays(rounds.map(r => (r.scheduledAt ?? NaN) - r.sourceAt)),
      ocr: summarizeDelays(rounds.map(r => (r.completedAt ?? NaN) - (r.scheduledAt ?? NaN))),
      completionToDelivery: summarizeDelays(rounds.map(r => r.deliveredAt - (r.completedAt ?? NaN))),
      sourceToAdoption: summarizeDelays(rounds.map(r => (r.acceptedAt ?? NaN) - r.sourceAt)) }
  }

  private expVerification = { rounds: 0, calls: [0, 0, 0, 0], conflicts: 0,
    totalDurationMs: 0, maxDurationMs: 0, accepted: 0, totalDeliveryDelayMs: 0, maxDeliveryDelayMs: 0 }
  observeExpVerification(calls: number, status: string, durationMs: number): void {
    if (calls < 1 || calls > 4 || status === 'cancelled') return
    const value = this.expVerification
    value.rounds++; value.calls[calls - 1] = (value.calls[calls - 1] ?? 0) + 1
    if (status === 'conflict') value.conflicts++
    value.totalDurationMs += durationMs; value.maxDurationMs = Math.max(value.maxDurationMs, durationMs)
  }
  observeExpAdoptionDelay(delayMs: number): void {
    if (!Number.isFinite(delayMs) || delayMs < 0) return
    const value = this.expVerification
    value.accepted++; value.totalDeliveryDelayMs += delayMs
    value.maxDeliveryDelayMs = Math.max(value.maxDeliveryDelayMs, delayMs)
  }
  private recordingReader: (() => RecordingDiagnostic) | null = null
  private recordingEvents: (RecordingEvent & { at: number; lastAt: number; count: number })[] = []
  private deliveryCounts: Partial<Record<DeliveryOutcome, number>> = {}
  private lastDelivery: { at: number; sourceAt: number; generation: number; outcome: DeliveryOutcome } | null = null
  private lastIngestedAt: number | null = null
  private lastCandidateAt: number | null = null
  private lastAcceptedAt: number | null = null
  setRecordingReader(reader: (() => RecordingDiagnostic) | null): void { this.recordingReader = reader }
  recordingEvent(event: RecordingEvent): void {
    const now = Date.now(), last = this.recordingEvents.at(-1)
    if (last && last.kind === event.kind && last.reason === event.reason && last.sessionId === event.sessionId && last.generation === event.generation) {
      last.lastAt = now; last.count++; return
    }
    this.recordingEvents.push({ ...event, at: now, lastAt: now, count: 1 })
    if (this.recordingEvents.length > MAX_EVENTS) this.recordingEvents.shift()
  }
  pipelineDelivery(outcome: DeliveryOutcome, sourceAt: number, generation: number): void {
    this.deliveryCounts[outcome] = (this.deliveryCounts[outcome] ?? 0) + 1
    this.lastDelivery = { at: Date.now(), sourceAt, generation, outcome }
    if (outcome === 'ingested') this.lastIngestedAt = this.lastDelivery.at
  }
  recordingExp(candidateAt: number | undefined, acceptedAt: number | null): void {
    if (candidateAt !== undefined) this.lastCandidateAt = candidateAt
    if (acceptedAt !== null) this.lastAcceptedAt = acceptedAt
  }

  private samplingReader: (() => import('./frame-scheduler').FrameSchedulerHealth | null) | null = null
  private captureRecoveryEvents: (CaptureRecoveryEvent & { at: number })[] = []
  setSamplingReader(reader: (() => import('./frame-scheduler').FrameSchedulerHealth | null) | null): void { this.samplingReader = reader }
  captureRecovery(event: CaptureRecoveryEvent): void {
    let capture: CapturePerformanceInfo | null = null
    try { capture = this.captureReader?.() ?? null } catch { /* source already released */ }
    this.captureRecoveryEvents.push(structuredClone({ ...event, capture, at: Date.now() }))
    if (this.captureRecoveryEvents.length > MAX_EVENTS) this.captureRecoveryEvents.shift()
  }
  private widgetContext: WidgetPerformanceContext | null = null
  private widgetRefreshes: { at: number; reason: WidgetRefreshReason; rendered: boolean; mode: WidgetPerformanceContext['mode']; mainVisibility: DocumentVisibilityState }[] = []
  private widgetRefreshCounts: Partial<Record<WidgetRefreshReason, { rendered: number; skipped: number }>> = {}
  setWidgetContext(value: WidgetPerformanceContext): void { this.widgetContext = { ...value } }
  closeWidget(): void {
    if (this.widgetContext) this.widgetContext = { ...this.widgetContext, open: false, mainVisibility: document.visibilityState }
  }
  widgetRefresh(reason: WidgetRefreshReason, rendered: boolean): void {
    const counts = this.widgetRefreshCounts[reason] ??= { rendered: 0, skipped: 0 }
    counts[rendered ? 'rendered' : 'skipped']++
    if (!this.widgetContext) return
    this.widgetRefreshes.push({ at: Date.now(), reason, rendered, mode: this.widgetContext.mode, mainVisibility: this.widgetContext.mainVisibility })
    if (this.widgetRefreshes.length > MAX_EVENTS) this.widgetRefreshes.shift()
  }
  private widgetFailures: (WidgetFailure & { at: number })[] = []
  widgetFailure(value: WidgetFailure): void {
    this.widgetFailures.push({ ...value, at: Date.now() })
    if (this.widgetFailures.length > MAX_EVENTS) this.widgetFailures.shift()
  }

  private resources: Partial<Record<PerformanceResource, { current: number; peak: number }>> = {}
  /** Return an idempotent release function; retain only numeric counts here. */
  acquire(resource: PerformanceResource): () => void {
    const value = this.resources[resource] ??= { current: 0, peak: 0 }
    value.current++
    value.peak = Math.max(value.peak, value.current)
    let released = false
    return () => {
      if (released) return
      released = true
      value.current--
    }
  }

  observeLongTasks(): () => void {
    if (typeof PerformanceObserver === 'undefined' || !PerformanceObserver.supportedEntryTypes?.includes('longtask')) return () => {}
    let observer: PerformanceObserver | null = null
    try {
      observer = new PerformanceObserver(list => {
        for (const entry of list.getEntries()) this.measure('long-task', entry.duration)
      })
      observer.observe({ type: 'longtask' })
    } catch { observer?.disconnect(); observer = null }
    return () => { observer?.disconnect(); observer = null }
  }
  private ocrStartup: OcrStartupDiagnostic | null = null
  setOcrStartup(value: OcrStartupDiagnostic): void { this.ocrStartup = structuredClone(value) }
  private capturePreparation: CapturePreparationDiagnostic | null = null
  setCapturePreparation(value: CapturePreparationDiagnostic): void { this.capturePreparation = structuredClone(value) }
  private layout: Partial<Record<'hud' | 'map' | 'mesos', LayoutSearchDiagnostic>> = {}
  observeLayout(group: 'hud' | 'map' | 'mesos', diagnostic: LayoutSearchDiagnostic): void {
    this.layout[group] = { ...diagnostic }
  }
  private metricSources = new Map<RecognitionMetricKey, number>()
  private metricIntervals = new Map<RecognitionMetricKey, { count: number; totalMs: number; lastMs: number; maxMs: number }>()
  private continuityBreaks: { at: string; metric: string; reason: string }[] = []
  private continuityCounts: Record<string, number> = {}
  private mapConfirmation: { state: string; reason?: string; expirationReason?: string; sampleGapMs?: number; windowMs?: number; expiresAt?: number } | null = null
  private startWait: { stage: string; since: number; budgetMs?: number } | null = null
  private expSession: string | null = null
  private expState: (ExpReadoutDiagnostic & { since: number }) | null = null
  private expIssueSince: number | null = null
  private expCounts: Record<string, number> = {}
  private expDurations: Record<string, number> = {}
  private expEvents: (ExpReadoutDiagnostic & { at: number; durationMs: number; recoveryMs: number | null; sessionRecoveryMs: number | null })[] = []
  private expChartSession: string | null = null
  private expChartEvents: ExpChartEvent[] = []
  private expRecognitionSession: string | null = null
  private expRecognition: ExpRecognitionProgress | null = null
  private expRecognitionEvents: ExpRecognitionEvent[] = []
  private recognitionContext: RecognitionContext | null = null
  setRecognitionContext(value: RecognitionContext): void { this.recognitionContext = { ...value } }
  private mesosRecognition: MesosRecognitionDiagnostic | null = null
  private mesosRecognitionEvents: MesosRecognitionDiagnostic[] = []
  observeMesosRecognition(profile: CaptureProfile, raw: RawReading, generation: number, sourceRevision: number): void {
    const diagnostic = raw.diagnostics.metrics?.mesos, completion = diagnostic?.completion
    const roi = profile.roiOverrides.mesos ?? diagnostic?.roi?.searchRect ?? profile.autoRois?.mesos?.rect ?? null
    const mode = profile.roiOverrides.mesos ? 'manual' : 'automatic'
    const previous = this.mesosRecognition
    const sameSource = previous !== null && previous.presetId === profile.presetId && previous.profileId === profile.id
      && previous.generation === generation && previous.sourceRevision === sourceRevision && previous.mode === mode
      && previous.roi?.x === roi?.x && previous.roi?.y === roi?.y
      && previous.roi?.width === roi?.width && previous.roi?.height === roi?.height
    const status = diagnostic?.status ?? 'no-result', now = Date.now()
    const current: MesosRecognitionDiagnostic = {
      presetId: profile.presetId, profileId: profile.id, generation, sourceRevision, mode,
      roi: roi ? { ...roi } : null, status, observedAt: now, frameAt: raw.capturedAt,
      sourceAt: completion?.capturedAt ?? diagnostic?.lastAttemptAt ?? null,
      lastQueuedAt: status === 'queued' && (!sameSource || previous?.status !== 'queued') ? now : sameSource ? previous.lastQueuedAt : null,
      lastScheduledAt: diagnostic?.scheduledAt ?? completion?.scheduledAt ?? (sameSource ? previous.lastScheduledAt : null),
      lastCompletedAt: completion?.completedAt ?? null, completionStatus: completion?.status ?? null,
      rawText: (completion?.rawText ?? diagnostic?.rawText ?? '').slice(0, 300),
      failureReason: diagnostic?.failureReason ?? completion?.failureReason
        ?? (completion && ['no-text', 'parse-failed', 'low-confidence'].includes(completion.status) ? completion.status : null),
      confidence: completion?.confidence ?? diagnostic?.confidence ?? null,
      value: typeof completion?.value === 'number' ? completion.value : null,
    }
    this.mesosRecognition = current
    // Metadata only: repeated frames update the current snapshot, never retain pixels.
    if (!sameSource || previous.status !== status || previous.failureReason !== current.failureReason
      || previous.completionStatus !== current.completionStatus) {
      this.mesosRecognitionEvents.push(current)
      if (this.mesosRecognitionEvents.length > 20) this.mesosRecognitionEvents.shift()
    }
  }
  private startedAt = Date.now()
  private timings = new Map<PerformanceStage, Timing>()
  private counters: Record<Counter, number> = { ocrInitializations: 0, ocrResets: 0, unchangedSkips: 0, recoveryBackoffs: 0 }
  private errorCounts: Partial<Record<DiagnosticError, number>> = {}
  private events: { at: string; code: DiagnosticError; retryInMs: number }[] = []
  private captureReader: (() => CapturePerformanceInfo) | null = null
  private processingIntervalMs: number | null = null

  observeMetric(metric: RecognitionMetricKey, sourceAt: number): void {
    if (!Number.isFinite(sourceAt)) return
    const previous = this.metricSources.get(metric)
    if (previous !== undefined && sourceAt <= previous) return
    this.metricSources.set(metric, sourceAt)
    if (previous === undefined) return
    const gap = sourceAt - previous
    const value = this.metricIntervals.get(metric) ?? { count: 0, totalMs: 0, lastMs: 0, maxMs: 0 }
    value.count += 1; value.totalMs += gap; value.lastMs = gap; value.maxMs = Math.max(value.maxMs, gap)
    this.metricIntervals.set(metric, value)
  }
  breakContinuity(metric: string, reason: PresenceBreakReason | 'legacy-timeout' | 'paused' | 'generation-changed'): void {
    const key = `${metric}:${reason}`
    this.continuityCounts[key] = (this.continuityCounts[key] ?? 0) + 1
    this.continuityBreaks.push({ at: new Date().toISOString(), metric, reason })
    if (this.continuityBreaks.length > MAX_EVENTS) this.continuityBreaks.shift()
  }
  setMapConfirmation(value: NonNullable<PerformanceDiagnostics['mapConfirmation']>): void { this.mapConfirmation = { ...value } }
  setStartWait(value: PerformanceDiagnostics['startWait']): void { this.startWait = value ? { ...value } : null }
  observeExpRecognition(sessionId: string, progress: ExpRecognitionProgress, now = Date.now()): void {
    if (sessionId !== this.expRecognitionSession) {
      this.expRecognitionSession = sessionId; this.expRecognition = null; this.expRecognitionEvents = []
    }
    const previous = this.expRecognition?.lastIssue
    const context = previous && progress.lastIssue?.sourceAt === previous.sourceAt
      && progress.lastIssue.kind === previous.kind && progress.lastIssue.reason === previous.reason
      ? previous.recognitionContext : this.recognitionContext
    const issue = progress.lastIssue ? { ...structuredClone(progress.lastIssue),
      ...(context ? { recognitionContext: { ...context } } : {}) } : null
    if (issue) {
      const last = this.expRecognitionEvents.at(-1)
      if (last && previous && last.recoveredAt === null && issue.kind === last.kind && issue.reason === last.reason
        && !(issue.kind === 'statistics' && last.kind === 'statistics' && issue.invalidReason !== last.invalidReason)
        && last.recognitionContext?.presetId === issue.recognitionContext?.presetId
        && last.recognitionContext?.modelName === issue.recognitionContext?.modelName
        && last.recognitionContext?.modelVersion === issue.recognitionContext?.modelVersion) {
        this.expRecognitionEvents[this.expRecognitionEvents.length - 1] = {
          ...(issue.sourceAt >= last.lastSourceAt ? issue : last), sourceAt: last.sourceAt, observedAt: last.observedAt,
          lastSourceAt: Math.max(last.lastSourceAt, issue.sourceAt), recoveredAt: null, recoverySourceAt: null,
        }
      } else {
        this.expRecognitionEvents.push({ ...issue, lastSourceAt: issue.sourceAt, recoveredAt: null, recoverySourceAt: null })
        if (this.expRecognitionEvents.length > MAX_EVENTS) this.expRecognitionEvents.shift()
      }
    } else if (progress.lastAcceptedSourceAt !== null) {
      for (const event of this.expRecognitionEvents) {
        if (event.recoveredAt === null && event.lastSourceAt <= progress.lastAcceptedSourceAt) {
          event.recoveredAt = now; event.recoverySourceAt = progress.lastAcceptedSourceAt
        }
      }
    }
    this.expRecognition = { ...progress, lastIssue: issue ? { ...issue } : null }
  }
  observeExpChart(sessionId: string, chart: ExpRateChartSnapshot | undefined, readout: ExpReadoutSnapshot, now = Date.now()): void {
    if (sessionId !== this.expChartSession) { this.expChartSession = sessionId; this.expChartEvents = [] }
    if (!chart) return
    for (const gap of chart.gaps) {
      let event = this.expChartEvents.find(e => e.reason === gap.reason && e.from === gap.from)
      if (!event) {
        event = { ...gap, observedAt: now, durationMs: 0, readoutReason: null, recoveredAt: null, recoverySourceAt: null }
        this.expChartEvents.push(event)
        if (this.expChartEvents.length > MAX_EVENTS) this.expChartEvents.shift()
      }
      event.to = gap.to
      event.durationMs = Math.max(0, gap.to - gap.from)
      if (gap.reason !== 'warming' && !event.recognitionIssue && sessionId === this.expRecognitionSession) {
        const issue = this.expRecognitionEvents.find(e => e.sourceAt >= gap.from && e.sourceAt <= gap.to)
        if (issue) event.recognitionIssue = { kind: issue.kind, reason: issue.reason, sourceAt: issue.sourceAt, observedAt: issue.observedAt } as ExpRecognitionIssue
      }
      if (gap.reason !== 'warming' && event.recoveredAt === null
        && gap.to >= (chart.recent.at ?? chart.to) && readout.reason) event.readoutReason ??= readout.reason
      const completed = gap.reason === 'warming' ? event.durationMs >= (chart.recent.windowMs ?? WINDOW_MS)
        : chart.recent.at !== null && chart.recent.at >= gap.to && readout.status === 'ready'
      if (completed && event.recoveredAt === null) {
        event.recoveredAt = now
        event.recoverySourceAt = chart.recent.at
      }
    }
  }
  observeExpReadout(sessionId: string, value: ExpReadoutSnapshot, now = Date.now()): void {
    if (sessionId !== this.expSession) {
      this.expSession = sessionId; this.expState = null; this.expIssueSince = null
    }
    const previous = this.expState
    const accounting = { incomplete: value.incomplete, incompleteSince: value.incompleteSince ?? null, incompleteReason: value.incompleteReason ?? null }
    if (previous?.status === value.status && previous.reason === value.reason && previous.scope === value.scope
      && previous.incomplete === accounting.incomplete && previous.incompleteSince === accounting.incompleteSince
      && previous.incompleteReason === accounting.incompleteReason) return
    const durationMs = previous ? Math.max(0, now - previous.since) : 0
    if (previous) this.expDurations[previous.status] = (this.expDurations[previous.status] ?? 0) + durationMs
    if (value.status !== 'ready') this.expIssueSince ??= now
    const recoveryMs = value.status === 'ready' && this.expIssueSince !== null ? Math.max(0, now - this.expIssueSince) : null
    if (value.status === 'ready') this.expIssueSince = null
    const key = `${value.status}:${value.reason ?? 'none'}`
    this.expCounts[key] = (this.expCounts[key] ?? 0) + 1
    const sessionRecoveryMs = previous?.incomplete && !value.incomplete
      ? Math.max(0, now - (previous.incompleteSince ?? previous.since)) : null
    this.expEvents.push({ at: now, status: value.status, reason: value.reason, scope: value.scope, ...accounting, durationMs, recoveryMs, sessionRecoveryMs })
    if (this.expEvents.length > MAX_EVENTS) this.expEvents.shift()
    this.expState = { status: value.status, reason: value.reason, scope: value.scope, ...accounting, since: now }
  }
  setCaptureReader(reader: () => CapturePerformanceInfo): void { this.captureReader = reader }
  setProcessingInterval(intervalMs: number | null): void { this.processingIntervalMs = intervalMs }
  increment(counter: Counter): void { this.counters[counter] += 1 }

  measure(stage: PerformanceStage, ms: number): void {
    if (!Number.isFinite(ms) || ms < 0) return
    const timing = this.timings.get(stage) ?? { count: 0, totalMs: 0, maxMs: 0, recent: [] }
    timing.count += 1
    timing.totalMs += ms
    timing.maxMs = Math.max(timing.maxMs, ms)
    timing.recent.push({ at: Date.now(), ms })
    if (timing.recent.length > MAX_SAMPLES) timing.recent.shift()
    this.timings.set(stage, timing)
  }

  error(code: DiagnosticError, retryInMs = 0): void {
    this.errorCounts[code] = (this.errorCounts[code] ?? 0) + 1
    this.events.push({ at: new Date().toISOString(), code, retryInMs })
    if (this.events.length > MAX_EVENTS) this.events.shift()
  }

  clear(): void {
    this.resourceDeliveryScope = ''; this.resourceDeliveries = { hp: [], mp: [] }
    this.expDeliveryScope = ''; this.expDeliveries = []
    this.expVerification = { rounds: 0, calls: [0, 0, 0, 0], conflicts: 0,
      totalDurationMs: 0, maxDurationMs: 0, accepted: 0, totalDeliveryDelayMs: 0, maxDeliveryDelayMs: 0 }
    this.recordingEvents = []; this.deliveryCounts = {}; this.lastDelivery = null
    this.lastIngestedAt = null; this.lastCandidateAt = null; this.lastAcceptedAt = null
    for (const value of Object.values(this.resources)) value.peak = value.current
    this.ocrStartup = null
    this.capturePreparation = null
    this.layout = {}
    this.startedAt = Date.now()
    this.metricSources.clear(); this.metricIntervals.clear(); this.continuityBreaks = []; this.continuityCounts = {}
    this.mapConfirmation = null; this.startWait = null
    this.expSession = null; this.expState = null; this.expIssueSince = null
    this.expCounts = {}; this.expDurations = {}; this.expEvents = []
    this.expChartSession = null; this.expChartEvents = []
    this.expRecognitionSession = null; this.expRecognition = null; this.expRecognitionEvents = []
    this.recognitionContext = null
    this.mesosRecognition = null; this.mesosRecognitionEvents = []
    this.timings.clear()
    this.counters = { ocrInitializations: 0, ocrResets: 0, unchangedSkips: 0, recoveryBackoffs: 0 }
    this.errorCounts = {}
    this.events = []
    this.widgetFailures = []
    this.captureRecoveryEvents = []
    this.widgetRefreshes = []; this.widgetRefreshCounts = {}
  }

  snapshot() {
    const now = Date.now()
    let capture: CapturePerformanceInfo | null = null
    try { capture = this.captureReader?.() ?? null } catch { /* closed or unavailable track */ }
    let sampling: import('./frame-scheduler').FrameSchedulerHealth | null = null
    try { sampling = this.samplingReader?.() ?? null } catch { /* pipeline is being disposed */ }
    let recording: RecordingDiagnostic | null = null
    try { recording = this.recordingReader?.() ?? null } catch { /* store already released */ }
    const timings = Object.fromEntries(stages.map(stage => {
      const timing = this.timings.get(stage)
      const recent = (timing?.recent ?? []).filter(sample => now - sample.at <= WINDOW_MS)
      const sorted = recent.map(sample => sample.ms).sort((a, b) => a - b)
      return [stage, {
        count: timing?.count ?? 0,
        averageMs: timing?.count ? round(timing.totalMs / timing.count) : null,
        maxMs: timing?.count ? round(timing.maxMs) : null,
        recentSampleCount: recent.length,
        recentP95Ms: sorted.length ? round(sorted[Math.ceil(sorted.length * .95) - 1]!) : null,
      }]
    }))
    return {
      schemaVersion: 2,
      expDelivery: this.expDeliverySnapshot(now),
      resourceDelivery: this.resourceDeliverySnapshot(now),
      expVerification: { ...this.expVerification, calls: [...this.expVerification.calls],
        hardCaseRate: this.expVerification.rounds
          ? ((this.expVerification.calls[2] ?? 0) + (this.expVerification.calls[3] ?? 0)) / this.expVerification.rounds : null },
      build: buildInfo,
      ocrStartup: this.ocrStartup ? structuredClone(this.ocrStartup) : null,
      capturePreparation: this.capturePreparation ? structuredClone(this.capturePreparation) : null,
      layout: structuredClone(this.layout),
      startedAt: new Date(this.startedAt).toISOString(),
      generatedAt: new Date(now).toISOString(),
      durationSeconds: round((now - this.startedAt) / 1_000),
      environment: { userAgent: navigator.userAgent, hardwareConcurrency: navigator.hardwareConcurrency ?? null, visibility: document.visibilityState, devicePixelRatio: window.devicePixelRatio ?? null },
      capture,
      sampling,
      recording: { current: recording ? { ...recording } : null, deliveryCounts: { ...this.deliveryCounts },
        lastDelivery: this.lastDelivery ? { ...this.lastDelivery } : null, lastIngestedAt: this.lastIngestedAt,
        lastCandidateSourceAt: this.lastCandidateAt, lastAcceptedSourceAt: this.lastAcceptedAt,
        recentEvents: this.recordingEvents.map(event => ({ ...event })) },
      captureRecovery: { current: recording?.captureRecovery ? structuredClone(recording.captureRecovery) : null,
        recentEvents: structuredClone(this.captureRecoveryEvents) },
      processingIntervalMs: this.processingIntervalMs,
      timings,
      resources: structuredClone(this.resources),
      // Chromium-only, sampled on export: excludes Worker, WASM and GPU allocations.
      mainThreadHeap: readMainThreadHeap(),
      metricReadingIntervals: Object.fromEntries([...this.metricIntervals].map(([key, value]) => [key, {
        count: value.count, averageMs: round(value.totalMs / value.count), lastMs: value.lastMs, maxMs: value.maxMs,
      }])),
      continuity: { counts: { ...this.continuityCounts }, recentBreaks: this.continuityBreaks.map(value => ({ ...value })) },
      mapConfirmation: this.mapConfirmation ? { ...this.mapConfirmation } : null,
      startWait: this.startWait ? { ...this.startWait } : null,
      expReadout: { current: this.expState ? { ...this.expState, durationMs: Math.max(0, now - this.expState.since) } : null,
        counts: { ...this.expCounts }, completedDurationMs: { ...this.expDurations }, recentChanges: this.expEvents.map(event => ({ ...event })) },
      expChart: { recentGaps: structuredClone(this.expChartEvents) },
      expRecognition: { current: this.expRecognition ? structuredClone(this.expRecognition) : null,
        ...(this.recognitionContext ? { recognitionContext: { ...this.recognitionContext } } : {}),
        recentIssues: structuredClone(this.expRecognitionEvents) },
      mesosRecognition: { current: this.mesosRecognition ? structuredClone(this.mesosRecognition) : null,
        recentChanges: structuredClone(this.mesosRecognitionEvents) },
      counters: { ...this.counters },
      errorCounts: { ...this.errorCounts },
      recentErrors: this.events.map(event => ({ ...event })),
      widgetFailures: this.widgetFailures.map(event => ({ ...event })),
      widget: { context: this.widgetContext ? { ...this.widgetContext } : null,
        refreshCounts: structuredClone(this.widgetRefreshCounts), recentRefreshes: this.widgetRefreshes.map(event => ({ ...event })) },
      limits: { recentWindowSeconds: WINDOW_MS / 1_000, maxSamplesPerStage: MAX_SAMPLES, maxRecentErrors: MAX_EVENTS },
    }
  }
}

function readMainThreadHeap() {
  const memory = (performance as Performance & { memory?: { usedJSHeapSize: number; totalJSHeapSize: number; jsHeapSizeLimit: number } }).memory
  if (!memory || !Number.isFinite(memory.usedJSHeapSize)) return null
  return { usedBytes: memory.usedJSHeapSize, allocatedBytes: memory.totalJSHeapSize, limitBytes: memory.jsHeapSizeLimit,
    scope: 'main-thread-js-heap-only' as const }
}

function round(value: number): number { return Math.round(value * 100) / 100 }
export const performanceDiagnostics = new PerformanceDiagnostics()
